import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gameItemOrder } from '@common/constants';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { type GameItemsType } from '@common/types/common';
import { H1 } from '@components/Typography/Typography';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { Picture } from '@components/ui/Picture/Picture';
import { selectStorageItemBySample } from '@redux/selectors/userSelectors';
import { useAppSelector } from '@redux/store';

import { BACKGROUNDS_CONFIG } from '@/common/config/media-config';

import cashPng from '/img/icons/cash.png';
import cashWebp from '/img/icons/cash.webp';

interface StorageHeadProps {
  kind: GameItemsType;
}

const backgrounds: Record<GameItemsType, { png: string; webp: string }> = {
  goods: BACKGROUNDS_CONFIG.BG_GOODS_STOCK,
  material: BACKGROUNDS_CONFIG.BG_MATERIAL_STOCK,
  resource: BACKGROUNDS_CONFIG.BG_RESOURCE_STOCK,
};

const StorageHead: FC<StorageHeadProps> = ({ kind }) => {
  const [totalAmount, setTotalAmount] = useState(0);
  const stockList = useAppSelector((state) =>
    selectStorageItemBySample(state, gameItemOrder[kind]),
  );
  const { t } = useTranslation();
  const titles = {
    resource: t('Resource stock'),
    material: t('Materials stock'),
    goods: t('Goods stock'),
  };

  useEffect(() => {
    if (stockList) {
      const totalAmount = stockList.reduce(
        (sum, { costPerUnit, value }) => sum + costPerUnit * value,
        0,
      );

      setTotalAmount(totalAmount);
    }
  }, [stockList]);

  return (
    <div className="w-full !h-[340px] flex items-end relative">
      <PageBackground
        className="absolute bottom-0 !h-[340px] left-0 -z-[1]"
        backgroundImage={backgrounds[kind]}
        variant="page"
      />
      <div className="w-full flex items-center justify-between px-2">
        <H1 weight="bold">{titles[kind]}</H1>
        {!!totalAmount && (
          <div className="flex items-center">
            <Picture
              className="w-6 h-6 shrink-0 mr-1"
              img={cashPng}
              webp={cashWebp}
              alt="cash"
            />
            <H1 weight="bold">{toShortNumFormat(totalAmount)}</H1>
          </div>
        )}
      </div>
    </div>
  );
};

export { StorageHead };
